<template>
    <div class="essential-plan" @click="selectPlan">
        <div class="left-section-pro">
            <div class="left-section-pro-header">
                <PrimeRadioButton @click="selectPlan" v-model="selectedPlanType" name="Essentials" :value="LicenseType.ESSENTIALS" />
                <div class="license-title-container">
                    <div class="license-name-price-container">
                        <div class="flex items-center">
                            <span>Essentials</span>
                            <span v-if="purchaseType === PurchaseType.GIFT && availableLicenses > 0" class="available-licenses-badge">
                                {{ availableLicenses }}
                            </span>
                        </div>
                        <div class="lg:hidden flex flex-col items-end" v-if="!store.isLoadingLicenseCosts && !(purchaseType === PurchaseType.GIFT && availableLicenses > 0)">
                            <span class="price-text">
                                {{ priceParts.dollars }}<span v-if="priceParts.cents !== null"><sup class="cents-sup">{{ priceParts.cents }}</sup></span>
                            </span>
                            <span class="user-time-text">User / {{ LicenseRenewalTermDisplay[selectedTerm] }}</span>
                        </div>
                    </div>
                    <span v-if="license?.slug === LicenseType.ESSENTIALS && purchaseType === PurchaseType.UPGRADE" class="current-license-badge">Current License</span>
                </div>
            </div>
            <p>Automated data removal with a human touch</p>
        </div>
        <div class="right-section-pro">
            <span v-if="store.isLoadingLicenseCosts" class="loading-price">
                <LoadingDots />
            </span>
            <span v-else-if="purchaseType === PurchaseType.GIFT && availableLicenses > 0">
                {{ availableLicenses }}  Available
            </span>
            <div v-else class="hidden lg:flex flex-col items-end text-right">
                <span class="price-text">
                    {{ priceParts.dollars }}<span v-if="priceParts.cents !== null"><sup class="cents-sup">{{ priceParts.cents }}</sup></span>
                </span>
                <span class="user-time-text">User / {{ LicenseRenewalTermDisplay[selectedTerm] }}</span>
            </div>
        </div>
    </div>

    <div class="benefits" v-if="selectedPlanType === LicenseType.ESSENTIALS">
        <div class="benefits-list">
            <div v-for="(benefit, index) in store.benefits.essentials" :key="index" class="benefit"> 
                <CheckCircleIcon class="check-icon" />
                <p>{{ benefit }}</p>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { CheckCircleIcon } from '@heroicons/vue/24/solid';
import PrimeRadioButton from 'primevue/radiobutton';
import LoadingDots from '~/components/main/shared/LoadingDots.vue';
import useUtils from '~/composables/useUtils';
import { useSubscribeStore } from '~/stores/components/modals/subscription-modal';
import { usePersonStore } from '~/stores/person';
import { LicenseRenewalTermDisplay, LicenseType, PurchaseType } from '~/types/licenses/licenses';

const store = useSubscribeStore();
const { license, selectedPlanType, purchaseType, selectedTerm } = storeToRefs(useSubscribeStore());
const { formatCurrency } = useUtils();
const personStore = usePersonStore();

// Compute available licenses for the selected plan type
const availableLicenses = computed(() => {
    if (purchaseType.value !== PurchaseType.GIFT) return 0;
    
    const availableLicenses = personStore.person?.licenses.filter(license => {
        return license.slug === LicenseType.ESSENTIALS && 
               license.term === selectedTerm.value && 
               license.expired === false && 
               !license.assigned_to;
    });
    
    return availableLicenses?.length || 0;
});

// Get total available licenses for this plan type across all timeframes
const getTotalAvailableLicenses = () => {
    if (!personStore.person?.licenses) return 0;
    
    return personStore.person.licenses.filter(license => {
        return license.slug === LicenseType.ESSENTIALS && 
               license.expired === false && 
               !license.assigned_to;
    }).length;
};

const priceParts = computed(() => {
    const formatted = formatCurrency(store.getCostPerLicenseAfterAnnualDiscount(LicenseType.ESSENTIALS));
    const match = formatted.match(/^(.*?)[.,](\d{2})$/);
    if (match) {
        return { dollars: match[1], cents: match[2] };
    }
    return { dollars: formatted, cents: null };
});
const selectPlan = () => {
    selectedPlanType.value = LicenseType.ESSENTIALS;
};
</script>

<style lang="scss" scoped>
.essential-plan {
    @apply flex justify-between items-start sm:px-8 sm:py-6 p-4;
    @apply border border-zinc-300 cursor-pointer transition-all duration-300 ease-in-out rounded-t-lg border-b-0;
    @apply flex-col lg:flex-row gap-4 lg:gap-0;

    &:hover {
        @apply -translate-y-[2px] bg-blue-10;
    }

    .left-section-pro {
        @apply flex-col;

        p {
            @apply text-zinc-600 text-sm font-normal mt-1 leading-6;
        }

        .left-section-pro-header {
            @apply flex items-center gap-3;

            .license-title-container {
                @apply flex flex-col gap-2 flex-1;

                .license-name-price-container {
                    @apply flex items-center justify-between w-full;

                    span:first-child {
                        @apply text-zinc-900 font-inter text-2xl font-semibold leading-8;
                    }

                    .price-text {
                        @apply text-zinc-900 font-inter text-2xl font-semibold leading-8 text-end;
                    }

                    .user-time-text {
                        @apply text-zinc-600 font-inter text-sm font-normal leading-6 text-end;
                    }
                }

                .current-license-badge {
                    @apply bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full inline-flex items-center justify-center min-w-0 w-auto flex-shrink-0;
                }

                .available-licenses-badge {
                    @apply bg-violet-500 text-white text-xs font-medium ml-2 px-2 py-1 rounded-full inline-flex items-center justify-center min-w-0 w-auto flex-shrink-0;
                }
            }
        }
    }

    .right-section-pro {
        @apply flex justify-end items-start;

        span {
            @apply text-zinc-900 font-inter text-2xl font-semibold leading-8 text-end;
        }

        .price-text {
            @apply text-zinc-900 font-inter text-2xl font-semibold leading-8 text-end;
        }

        .user-time-text {
            @apply text-zinc-600 font-inter text-sm font-normal leading-6 text-end;
        }
    }
}

.benefits {
    @apply flex flex-col items-start gap-6 self-stretch sm:px-8 sm:py-8 p-4 border border-zinc-300 border-t-0;
}
.benefits-list {
    @apply flex flex-col items-start gap-y-4 self-stretch;
    @apply lg:flex-row lg:flex-wrap lg:gap-x-4;
}
.benefit {
    @apply flex items-start gap-3 flex-1;

    p {
        @apply text-zinc-600 font-inter text-sm font-normal leading-6 w-full lg:w-60;
    }
}
.check-icon {
    @apply w-6 h-6 text-green-500;
}

.cents-sup {
    @apply text-xs align-super leading-none relative -top-1;
}
</style>
